import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductGrid } from "../product";
import { SEO } from "../SEO";

const NFL_TEAMS = ["Bills", "Bears", "Bengals", "Browns", "Panthers", "Broncos", "Cowboys", "Eagles", "Falcons", "49ers", "Giants", "Jaguars", "Jets", "Chiefs", "Chargers", "Rams", "Dolphins", "Vikings", "Patriots", "Saints", "Raiders", "Packers", "Steelers", "Ravens", "Seahawks", "Bucs", "Titans", "Commanders", "Texans", "Colts", "Lions", "Cardinals"];

const MLB_TEAMS = ["Yankees", "Red Sox", "Dodgers", "Cubs", "Mets", "Braves", "Astros", "Cardinals", "Giants", "Padres", "Phillies", "Mariners", "Rangers", "Orioles", "Guardians", "Twins", "Brewers", "Rays", "Blue Jays", "White Sox", "Reds", "Pirates", "Tigers", "Royals", "Rockies", "Angels", "Athletics", "Marlins", "Nationals", "Diamondbacks"];

function classifySport(name) {
  const upper = name.toUpperCase();
  for (const team of NFL_TEAMS) {
    if (upper.includes(team.toUpperCase())) return "Football";
  }
  for (const team of MLB_TEAMS) {
    if (upper.includes(team.toUpperCase())) return "Baseball";
  }
  return "Basketball";
}

const SPORT_CONFIG = {
  "All": { color: "purple", emoji: "", label: "All Sports" },
  "Football": { color: "green", emoji: "", label: "Football" },
  "Basketball": { color: "orange", emoji: "", label: "Basketball" },
  "Baseball": { color: "red", emoji: "", label: "Baseball" },
};

export function SportsSection({ products, isPage, sportFilter }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(sportFilter || "All");
  const sportsProducts = products.filter(p => p.collection === "Sports");

  const grouped = { Football: [], Basketball: [], Baseball: [] };
  sportsProducts.forEach(p => {
    const sport = classifySport(p.name);
    grouped[sport].push(p);
  });

  const availableSports = Object.entries(grouped).filter(([, items]) => items.length > 0);
  const filteredProducts = activeTab === "All" ? sportsProducts : (grouped[activeTab] || []);

  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-black via-purple-950/10 to-black`} data-testid="sports-section">
      <div className="max-w-7xl mx-auto">
        {/* NFL Hero Video - shown on Football tab or All */}
        {isPage && (activeTab === "All" || activeTab === "Football") && (
          <div className="mb-12 max-w-4xl mx-auto rounded-2xl overflow-hidden border border-green-500/20 shadow-2xl shadow-green-900/20">
            <video
              src="https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/jolopavi_FreshPoboy_NFL_Ad_Final.mp4"
              className="w-full aspect-video object-cover"
              autoPlay
              muted
              loop
              playsInline
              data-testid="football-hero-video"
            />
          </div>
        )}

        {/* Basketball Hero Video - shown on Basketball tab */}
        {isPage && activeTab === "Basketball" && (
          <div className="mb-12 max-w-4xl mx-auto rounded-2xl overflow-hidden border border-orange-500/20 shadow-2xl shadow-orange-900/20">
            <video
              src="https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/qmfbniam_basketball_banner.mp4"
              className="w-full aspect-video object-cover"
              autoPlay
              muted
              loop
              playsInline
              data-testid="basketball-hero-video"
            />
          </div>
        )}

        <div className="text-center mb-12">
          {isPage && <SEO title="Sports Collection" description="Fresh Po'Boy Sports Collection. NFL Football, NBA Basketball & more. Rep your team with premium streetwear." path="/sports" />}
          <span className="text-purple-400 text-sm tracking-widest uppercase">Game Day</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Sports <span className="text-purple-400">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Rep your team. Own the game. Premium sports-inspired streetwear.</p>
        </div>

        {isPage && (
          <>
            {/* Sport Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="sport-tabs">
              {["All", ...availableSports.map(([s]) => s)].map(sport => {
                const config = SPORT_CONFIG[sport];
                const isActive = activeTab === sport;
                const count = sport === "All" ? sportsProducts.length : (grouped[sport]?.length || 0);
                return (
                  <button
                    key={sport}
                    onClick={() => setActiveTab(sport)}
                    className={`px-6 py-3 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${
                      isActive
                        ? `bg-${config.color}-500 text-white shadow-lg shadow-${config.color}-500/30`
                        : `bg-zinc-900 text-white/60 border border-white/10 hover:border-${config.color}-500/50 hover:text-white`
                    }`}
                    style={isActive ? { backgroundColor: sport === "All" ? "#9333ea" : sport === "Football" ? "#16a34a" : sport === "Basketball" ? "#ea580c" : "#dc2626" } : {}}
                    data-testid={`sport-tab-${sport.toLowerCase()}`}
                  >
                    {config.label}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-white/10'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Active Sport Header */}
            {activeTab !== "All" && (
              <div className="text-center mb-10">
                <h3 className="text-white text-3xl sm:text-4xl font-bebas">
                  {activeTab === "Football" && <span className="text-green-400">Football</span>}
                  {activeTab === "Basketball" && <span className="text-orange-400">Basketball</span>}
                  {activeTab === "Baseball" && <span className="text-red-400">Baseball</span>}
                </h3>
                <p className="text-white/40 text-sm mt-1">{filteredProducts.length} items</p>
              </div>
            )}

            <ProductGrid products={filteredProducts} />

            {/* Sport Sections when showing All */}
            {activeTab === "All" && availableSports.length > 1 && (
              <div className="mt-16 space-y-16">
                {availableSports.map(([sport, items]) => (
                  <div key={sport} data-testid={`sport-section-${sport.toLowerCase()}`}>
                    <div className="flex items-center gap-4 mb-8">
                      <div className={`h-px flex-1 ${sport === "Football" ? "bg-green-500/30" : sport === "Basketball" ? "bg-orange-500/30" : "bg-red-500/30"}`}></div>
                      <h3 className="text-white text-3xl font-bebas flex items-center gap-2">
                        {sport === "Football" && <span className="text-green-400">Football</span>}
                        {sport === "Basketball" && <span className="text-orange-400">Basketball</span>}
                        {sport === "Baseball" && <span className="text-red-400">Baseball</span>}
                        <span className="text-white/30 text-lg ml-2">({items.length})</span>
                      </h3>
                      <div className={`h-px flex-1 ${sport === "Football" ? "bg-green-500/30" : sport === "Basketball" ? "bg-orange-500/30" : "bg-red-500/30"}`}></div>
                    </div>
                    <ProductGrid products={items} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!isPage && (
          <>
            <ProductGrid products={sportsProducts.slice(0, 8)} />
            {sportsProducts.length > 0 && (
              <div className="text-center mt-12">
                <button onClick={() => navigate('/sports')} className="px-8 py-4 bg-purple-600 text-white font-semibold hover:bg-purple-500 transition-colors" data-testid="view-sports-collection-btn">View Full Collection</button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
